/**
 * Docking Station Settings — demo-only ticket-body component.
 *
 * Renders the metadata + initial-setup form for a registered DJI dock. The
 * platform never connects to the dock directly (see project memory
 * `project_dock_mission_architecture.md` and `docs/DOCK_MISSION_PLAN.md`); the
 * settings here describe "which physical dock will fly missions for this
 * site, who operates it, and what the default flight envelope is".
 *
 * Sections in render order:
 *   1. Stations list  — in-memory CRUD over registered docks
 *   2. Identity       — name, serial, model, firmware
 *   3. Location       — lat / lng / elevation, "Use map center" helper
 *   4. Drone          — bound aircraft model, serial, payload checkboxes
 *   5. Flight defaults— RTH alt, max alt, max speed, max distance
 *   6. Geofence       — circular fence radius
 *   7. Operator       — name, company, email, phone for dispatch handoff
 *   8. Status         — read-only, demo placeholders for operator-pushed data
 *   9. Actions        — Save (no-op visible toast), Send setup checklist,
 *                       Calibrate, Test KMZ flow, Delete dock
 */

import { useEffect, useState } from 'react';
import type { MapMouseEvent } from 'mapbox-gl';
import { useDockDemoStore } from '../../stores/dockDemoStore';
import { useUIStore, useMapStore } from '../../stores';
import Collapsible from './Collapsible';
import { useDemoNFZDraw } from './useDemoNFZDraw';
import type {
  DemoDockStation,
  DemoDockModel,
  DemoDroneModel,
  DemoDronePayload,
} from './types';

const DOCK_MODELS: DemoDockModel[] = ['dock2', 'dock3'];
const DRONE_MODELS: DemoDroneModel[] = ['M3D', 'M3TD', 'M30T'];
const PAYLOADS: DemoDronePayload[] = ['rgb', 'thermal', 'zoom'];

function fmtCoord(value: number): string {
  return value.toFixed(6);
}

export default function DockStationsDemoPanel() {
  const stations = useDockDemoStore((s) => s.dockStations);
  const selectedId = useDockDemoStore((s) => s.selectedDockStationId);

  const createDockStation = useDockDemoStore((s) => s.createDockStation);
  const deleteDockStation = useDockDemoStore((s) => s.deleteDockStation);
  const selectDockStation = useDockDemoStore((s) => s.selectDockStation);
  const updateDockStation = useDockDemoStore((s) => s.updateDockStation);

  const map = useMapStore((s) => s.map);
  const addNotification = useUIStore((s) => s.addNotification);
  const showConfirm = useUIStore((s) => s.showConfirm);
  const hideConfirm = useUIStore((s) => s.hideConfirm);

  // NFZ draw mode is driven from the store (transient), not local state, so
  // the cursor / click handlers stay alive even if the user closes the panel
  // mid-draw. The hook below captures map clicks while in draw mode.
  const nfzDrawMode = useDockDemoStore((s) => s.nfzDrawMode);
  const nfzDrawVertices = useDockDemoStore((s) => s.nfzDrawVertices);
  const nfzEditingId = useDockDemoStore((s) => s.nfzEditingId);
  const setNfzDrawMode = useDockDemoStore((s) => s.setNfzDrawMode);
  const clearNfzDraft = useDockDemoStore((s) => s.clearNfzDraft);
  const setNfzEditingId = useDockDemoStore((s) => s.setNfzEditingId);
  const addNoFlyZone = useDockDemoStore((s) => s.addNoFlyZone);
  const updateNoFlyZone = useDockDemoStore((s) => s.updateNoFlyZone);
  const deleteNoFlyZone = useDockDemoStore((s) => s.deleteNoFlyZone);
  useDemoNFZDraw();

  const [pickingLocation, setPickingLocation] = useState(false);
  const [placingPolygon, setPlacingPolygon] = useState(false);

  const selected = stations.find((s) => s.id === selectedId) ?? null;

  // ── Polygon vertex placement ───────────────────────────────────────────
  // While `placingPolygon` is on, every map click appends a vertex to the
  // selected dock's geofence. Click the toggle again to stop. The hook
  // detaches the listener and resets the cursor on cleanup so a panel close
  // mid-placement leaves the map in a sane state.
  useEffect(() => {
    if (!placingPolygon || !map || !selected) return;
    if (selected.geofence.type !== 'polygon') return;

    const handler = (e: MapMouseEvent) => {
      // Re-read store state on each click — `selected` is captured by the
      // effect, but the latest geofence array lives in the store.
      const current = useDockDemoStore.getState().dockStations.find((d) => d.id === selected.id);
      if (!current || current.geofence.type !== 'polygon') return;
      updateDockStation(selected.id, {
        geofence: {
          type: 'polygon',
          coordinates: [...current.geofence.coordinates, [e.lngLat.lng, e.lngLat.lat]],
        },
      });
    };

    const canvas = map.getCanvas();
    const previousCursor = canvas.style.cursor;
    canvas.style.cursor = 'crosshair';
    map.on('click', handler);

    return () => {
      map.off('click', handler);
      canvas.style.cursor = previousCursor;
    };
  }, [placingPolygon, map, selected, updateDockStation]);

  // If the user switches dock or geofence type, abort polygon placement.
  useEffect(() => {
    if (!selected || selected.geofence.type !== 'polygon') {
      setPlacingPolygon(false);
    }
  }, [selected?.id, selected?.geofence.type]);

  // ── Mission-relative actions ────────────────────────────────────────────

  const handleNewStation = () => {
    createDockStation('Dock ' + (stations.length + 1));
  };

  const handleDeleteStation = (id: string, name: string) => {
    showConfirm({
      title: 'Delete dock station?',
      message: `"${name}" will be removed. Missions referencing it will lose their home dock.`,
      variant: 'danger',
      confirmLabel: 'Delete',
      onConfirm: () => {
        deleteDockStation(id);
        hideConfirm();
      },
    });
  };

  const handleUseMapCenter = () => {
    if (!selected || !map) return;
    const center = map.getCenter();
    updateDockStation(selected.id, { lng: center.lng, lat: center.lat });
    addNotification({
      type: 'success',
      title: 'Location captured',
      message: `Dock home set to ${fmtCoord(center.lng)}, ${fmtCoord(center.lat)}.`,
      duration: 3500,
    });
  };

  const handlePickOnMap = () => {
    if (!selected || !map) return;
    setPickingLocation(true);
    const onClick = (e: MapMouseEvent) => {
      updateDockStation(selected.id, { lng: e.lngLat.lng, lat: e.lngLat.lat });
      map.off('click', onClick);
      map.getCanvas().style.cursor = '';
      setPickingLocation(false);
      addNotification({
        type: 'success',
        title: 'Dock location set',
        message: `${fmtCoord(e.lngLat.lng)}, ${fmtCoord(e.lngLat.lat)}`,
        duration: 3500,
      });
    };
    map.getCanvas().style.cursor = 'crosshair';
    map.once('click', onClick);
  };

  const handleSendSetupChecklist = () => {
    addNotification({
      type: 'info',
      title: 'Setup checklist sent',
      message: 'The dock setup checklist (network config, RTK base, geofence) has been emailed to the operator.',
      duration: 7000,
    });
  };

  const handleCalibrate = () => {
    addNotification({
      type: 'info',
      title: 'Calibration scheduled',
      message: 'Calibration runs on the physical dock and is operator-side. The platform records the run timestamp.',
      duration: 6000,
    });
  };

  const handleTestKmz = () => {
    addNotification({
      type: 'info',
      title: 'Test flight sent',
      message: 'A 2-waypoint test KMZ has been emailed to the operator as an acceptance check.',
      duration: 6000,
    });
  };

  // ── Patch helpers (nested objects) ──────────────────────────────────────

  const patchDrone = (drone: Partial<DemoDockStation['drone']>) => {
    if (!selected) return;
    updateDockStation(selected.id, {
      drone: { ...selected.drone, ...drone },
    });
  };
  const patchDefaults = (defaults: Partial<DemoDockStation['defaults']>) => {
    if (!selected) return;
    updateDockStation(selected.id, {
      defaults: { ...selected.defaults, ...defaults },
    });
  };
  const patchOperator = (operator: Partial<DemoDockStation['operator']>) => {
    if (!selected) return;
    updateDockStation(selected.id, {
      operator: { ...selected.operator, ...operator },
    });
  };

  const togglePayload = (p: DemoDronePayload) => {
    if (!selected) return;
    const has = selected.drone.payload.includes(p);
    patchDrone({
      payload: has
        ? selected.drone.payload.filter((x) => x !== p)
        : [...selected.drone.payload, p],
    });
  };

  return (
    <>
      <div className="dock-demo-ticket">
      {/* ── 1. Stations list ── */}
      <Collapsible
        title="Stations"
        action={
          <button
            type="button"
            className="dock-demo-btn dock-demo-btn--primary"
            onClick={handleNewStation}
          >
            + Register
          </button>
        }
      >
        {stations.length === 0 ? (
          <p className="dock-demo-empty">
            No docks registered yet. Click <strong>Register</strong> to add one.
          </p>
        ) : (
          <ul className="dock-demo-list">
            {stations.map((d) => (
              <li
                key={d.id}
                className={`dock-demo-list__item ${d.id === selectedId ? 'dock-demo-list__item--active' : ''}`}
                onClick={() => selectDockStation(d.id)}
              >
                <div className="dock-demo-list__main">
                  <span className="dock-demo-list__name">{d.name}</span>
                  <span className="dock-demo-list__meta">
                    {d.model} · {d.serial || '— no serial —'}
                  </span>
                </div>
                <button
                  type="button"
                  className="dock-demo-list__del"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteStation(d.id, d.name);
                  }}
                  title="Delete dock"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </Collapsible>

      {selected && (
        <>
          {/* ── 2. Identity ── */}
          <Collapsible title="Identity">
            <label className="dock-demo-label">
              Name
              <input
                type="text"
                className="dock-demo-input"
                value={selected.name}
                onChange={(e) => updateDockStation(selected.id, { name: e.target.value })}
              />
            </label>

            <div className="dock-demo-grid-2">
              <label className="dock-demo-label">
                Serial
                <input
                  type="text"
                  className="dock-demo-input"
                  placeholder="e.g. 1581F5..."
                  value={selected.serial}
                  onChange={(e) => updateDockStation(selected.id, { serial: e.target.value })}
                />
              </label>
              <label className="dock-demo-label">
                Model
                <select
                  className="dock-demo-input"
                  value={selected.model}
                  onChange={(e) =>
                    updateDockStation(selected.id, { model: e.target.value as DemoDockModel })
                  }
                >
                  {DOCK_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
            </div>

            <label className="dock-demo-label">
              Firmware
              <input
                type="text"
                className="dock-demo-input"
                placeholder="e.g. 06.00.0100"
                value={selected.firmware ?? ''}
                onChange={(e) => updateDockStation(selected.id, { firmware: e.target.value })}
              />
            </label>
          </Collapsible>

          {/* ── 3. Location ── */}
          <Collapsible title="Location">
            <div className="dock-demo-grid-2">
              <label className="dock-demo-label">
                Longitude
                <input
                  type="number"
                  step="any"
                  className="dock-demo-input"
                  value={selected.lng || ''}
                  onChange={(e) =>
                    updateDockStation(selected.id, { lng: Number(e.target.value) || 0 })
                  }
                />
              </label>
              <label className="dock-demo-label">
                Latitude
                <input
                  type="number"
                  step="any"
                  className="dock-demo-input"
                  value={selected.lat || ''}
                  onChange={(e) =>
                    updateDockStation(selected.id, { lat: Number(e.target.value) || 0 })
                  }
                />
              </label>
            </div>

            <div className="dock-demo-grid-2">
              <label className="dock-demo-label">
                Elevation (m MSL)
                <input
                  type="number"
                  className="dock-demo-input"
                  placeholder="auto"
                  value={selected.elevation ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    updateDockStation(selected.id, {
                      elevation: v === '' ? undefined : Number(v),
                    });
                  }}
                />
              </label>
              <label className="dock-demo-label">
                Site label
                <input
                  type="text"
                  className="dock-demo-input"
                  placeholder="e.g. Sorek roof"
                  value={selected.siteLabel ?? ''}
                  onChange={(e) =>
                    updateDockStation(selected.id, { siteLabel: e.target.value })
                  }
                />
              </label>
            </div>

            <div className="dock-demo-actions">
              <button
                type="button"
                className="dock-demo-btn"
                onClick={handleUseMapCenter}
                disabled={!map}
              >
                Use map center
              </button>
              <button
                type="button"
                className={`dock-demo-btn ${pickingLocation ? 'dock-demo-btn--active' : ''}`}
                onClick={handlePickOnMap}
                disabled={!map}
              >
                {pickingLocation ? '✓ Click map…' : 'Pick on map'}
              </button>
            </div>
          </Collapsible>

          {/* ── 4. Drone ── */}
          <Collapsible title="Drone">
            <div className="dock-demo-grid-2">
              <label className="dock-demo-label">
                Model
                <select
                  className="dock-demo-input"
                  value={selected.drone.model}
                  onChange={(e) => patchDrone({ model: e.target.value as DemoDroneModel })}
                >
                  {DRONE_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
              <label className="dock-demo-label">
                Serial
                <input
                  type="text"
                  className="dock-demo-input"
                  placeholder="aircraft serial"
                  value={selected.drone.serial ?? ''}
                  onChange={(e) => patchDrone({ serial: e.target.value })}
                />
              </label>
            </div>

            <div className="dock-demo-label">
              <span>Payload</span>
              <div className="dock-demo-checkrow">
                {PAYLOADS.map((p) => (
                  <label className="dock-demo-checkbox" key={p}>
                    <input
                      type="checkbox"
                      checked={selected.drone.payload.includes(p)}
                      onChange={() => togglePayload(p)}
                    />
                    <span>{p}</span>
                  </label>
                ))}
              </div>
            </div>
          </Collapsible>

          {/* ── 5. Flight defaults ── */}
          <Collapsible title="Flight defaults" sub="used when planning">
            <div className="dock-demo-grid-2">
              <label className="dock-demo-label">
                RTH altitude (m)
                <input
                  type="number"
                  min={20}
                  max={500}
                  className="dock-demo-input"
                  value={selected.defaults.rthAltM}
                  onChange={(e) => patchDefaults({ rthAltM: Number(e.target.value) || 0 })}
                />
              </label>
              <label className="dock-demo-label">
                Max altitude (m)
                <input
                  type="number"
                  min={20}
                  max={500}
                  className="dock-demo-input"
                  value={selected.defaults.maxAltM}
                  onChange={(e) => patchDefaults({ maxAltM: Number(e.target.value) || 0 })}
                />
              </label>
              <label className="dock-demo-label">
                Max speed (m/s)
                <input
                  type="number"
                  min={1}
                  max={15}
                  className="dock-demo-input"
                  value={selected.defaults.maxSpeedMps}
                  onChange={(e) => patchDefaults({ maxSpeedMps: Number(e.target.value) || 0 })}
                />
              </label>
              <label className="dock-demo-label">
                Max distance (m)
                <input
                  type="number"
                  min={100}
                  max={8000}
                  className="dock-demo-input"
                  value={selected.defaults.maxDistanceM}
                  onChange={(e) => patchDefaults({ maxDistanceM: Number(e.target.value) || 0 })}
                />
              </label>
            </div>
          </Collapsible>

          {/* ── 6. Geofence ── */}
          <Collapsible
            title="Geofence"
            sub={selected.geofence.type === 'radius' ? 'circular' : 'polygon'}
          >
            {/* Per-dock visibility toggle. Hides the ring/polygon + its
                vertices on the map without deleting the fence definition.
                Useful when several docks share a viewport and the user
                wants a less busy view. The home marker stays visible. */}
            <label className="dock-demo-checkbox dock-demo-checkbox--standalone">
              <input
                type="checkbox"
                checked={selected.geofenceVisible !== false}
                onChange={(e) =>
                  updateDockStation(selected.id, { geofenceVisible: e.target.checked })
                }
              />
              <span>Show geofence on map</span>
            </label>

            <label className="dock-demo-label">
              Type
              <select
                className="dock-demo-input"
                value={selected.geofence.type}
                onChange={(e) => {
                  const t = e.target.value as 'radius' | 'polygon';
                  // Switching type wipes the other variant's data — by design,
                  // the two are different operational shapes and merging them
                  // would produce an invalid fence.
                  updateDockStation(selected.id, {
                    geofence: t === 'radius'
                      ? { type: 'radius', radiusM: 1500 }
                      : { type: 'polygon', coordinates: [] },
                  });
                  setPlacingPolygon(false);
                }}
              >
                <option value="radius">Radius (circular)</option>
                <option value="polygon">Polygon</option>
              </select>
            </label>

            {selected.geofence.type === 'radius' && (
              <label className="dock-demo-label">
                Radius (m)
                <input
                  type="number"
                  min={50}
                  max={8000}
                  className="dock-demo-input"
                  value={selected.geofence.radiusM}
                  onChange={(e) =>
                    updateDockStation(selected.id, {
                      geofence: {
                        type: 'radius',
                        radiusM: Number(e.target.value) || 0,
                      },
                    })
                  }
                />
              </label>
            )}

            {selected.geofence.type === 'polygon' && (
              <>
                <div className="dock-demo-summary__row">
                  <span>
                    <strong>{selected.geofence.coordinates.length}</strong>{' '}
                    {selected.geofence.coordinates.length === 1 ? 'vertex' : 'vertices'}
                  </span>
                  {selected.geofence.coordinates.length < 3 && (
                    <span className="dock-demo-tag dock-demo-tag--warn">
                      need 3+ to close
                    </span>
                  )}
                </div>
                <div className="dock-demo-actions">
                  <button
                    type="button"
                    className={`dock-demo-btn ${placingPolygon ? 'dock-demo-btn--active' : ''}`}
                    onClick={() => setPlacingPolygon((p) => !p)}
                    disabled={!map}
                  >
                    {placingPolygon ? '✓ Click map…' : 'Place vertices'}
                  </button>
                  <button
                    type="button"
                    className="dock-demo-btn"
                    disabled={selected.geofence.coordinates.length === 0}
                    onClick={() => {
                      if (selected.geofence.type !== 'polygon') return;
                      updateDockStation(selected.id, {
                        geofence: {
                          type: 'polygon',
                          coordinates: selected.geofence.coordinates.slice(0, -1),
                        },
                      });
                    }}
                  >
                    Remove last
                  </button>
                  <button
                    type="button"
                    className="dock-demo-btn dock-demo-btn--ghost"
                    disabled={selected.geofence.coordinates.length === 0}
                    onClick={() =>
                      updateDockStation(selected.id, {
                        geofence: { type: 'polygon', coordinates: [] },
                      })
                    }
                  >
                    Clear all
                  </button>
                </div>
              </>
            )}

            <p className="dock-demo-hint">
              {selected.geofence.type === 'radius'
                ? 'Mission planner refuses to export a KMZ whose waypoints fall outside this radius from the dock home point.'
                : 'Click "Place vertices" then click on the map to add corners. The polygon closes back to the first vertex automatically once 3+ corners are set.'}
            </p>
          </Collapsible>

          {/* ── 6b. No-fly zones — obstacle polygons inside the geofence.
              Validation blocks Send/Export when a route crosses an NFZ;
              the auto-router (Phase 4) inserts detour waypoints around it. */}
          <Collapsible
            title="No-fly zones"
            sub={`${(selected.noFlyZones ?? []).length} zones`}
            defaultOpen
          >
            {nfzDrawMode ? (
              <>
                <p className="dock-demo-hint">
                  Click the map to add vertices ({nfzDrawVertices.length} placed).
                  Press <strong>Enter</strong> to finalize ({nfzDrawVertices.length < 3
                    ? `need ${3 - nfzDrawVertices.length} more`
                    : 'ready'}) or <strong>Esc</strong> to cancel.
                </p>
                <div className="dock-demo-actions" style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="dock-demo-btn"
                    disabled={nfzDrawVertices.length < 3}
                    onClick={() => {
                      addNoFlyZone(selected.id, nfzDrawVertices);
                    }}
                  >
                    Finish drawing
                  </button>
                  <button
                    type="button"
                    className="dock-demo-btn"
                    onClick={clearNfzDraft}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="dock-demo-hint">
                  Draw polygon obstacles the drone should route around. Send/Export
                  blocks when a leg crosses a visible no-fly zone.
                </p>
                <div className="dock-demo-actions">
                  <button
                    type="button"
                    className="dock-demo-btn"
                    onClick={() => {
                      // Mutex with the geofence polygon-placement mode — only
                      // one map drawing tool can own clicks at a time.
                      if (placingPolygon) setPlacingPolygon(false);
                      setNfzDrawMode(true);
                    }}
                  >
                    + Add no-fly zone
                  </button>
                </div>

                {(selected.noFlyZones ?? []).length > 0 && (
                  <ul className="dock-demo-list" style={{ marginTop: 10 }}>
                    {(selected.noFlyZones ?? []).map((nfz, idx) => {
                      const isEditing = nfzEditingId === nfz.id;
                      return (
                        <li
                          key={nfz.id}
                          className={`dock-demo-list__item ${isEditing ? 'dock-demo-list__item--active' : ''}`}
                          // Override the shared list item's row layout — NFZ
                          // entries need a name input on top + controls row
                          // below, plus an optional edit-mode hint.
                          style={{
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            cursor: 'default',
                            gap: 6,
                          }}
                        >
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input
                              type="text"
                              className="dock-demo-input"
                              value={nfz.name ?? ''}
                              placeholder={`No-fly zone ${idx + 1}`}
                              onChange={(e) =>
                                updateNoFlyZone(selected.id, nfz.id, { name: e.target.value })
                              }
                              style={{ flex: 1, minWidth: 0 }}
                            />
                            <span className="dock-demo-tag">{nfz.vertices.length} pts</span>
                          </div>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <label
                              className="dock-demo-checkbox"
                              style={{ flex: 1, minWidth: 0 }}
                            >
                              <input
                                type="checkbox"
                                checked={nfz.visible !== false}
                                onChange={(e) =>
                                  updateNoFlyZone(selected.id, nfz.id, {
                                    visible: e.target.checked,
                                  })
                                }
                              />
                              <span>Visible</span>
                            </label>
                            <button
                              type="button"
                              className={`dock-demo-btn ${isEditing ? 'dock-demo-btn--active' : ''}`}
                              onClick={() => setNfzEditingId(isEditing ? null : nfz.id)}
                              title={
                                isEditing
                                  ? 'Done editing vertices'
                                  : 'Show vertex handles on the map'
                              }
                            >
                              {isEditing ? '✓ Done' : 'Edit'}
                            </button>
                            <button
                              type="button"
                              className="dock-demo-list__del"
                              onClick={() => deleteNoFlyZone(selected.id, nfz.id)}
                              title="Delete no-fly zone"
                            >
                              ×
                            </button>
                          </div>
                          {isEditing && (
                            <p className="dock-demo-hint" style={{ margin: 0 }}>
                              Drag a handle to move a vertex; click the outline
                              to add a vertex; right-click a handle to delete
                              it (minimum 3).
                            </p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}
          </Collapsible>

          {/* ── 7. Operator ── */}
          <Collapsible title="Operator" sub="dispatch contact">
            <div className="dock-demo-grid-2">
              <label className="dock-demo-label">
                Name
                <input
                  type="text"
                  className="dock-demo-input"
                  value={selected.operator.name ?? ''}
                  onChange={(e) => patchOperator({ name: e.target.value })}
                />
              </label>
              <label className="dock-demo-label">
                Company
                <input
                  type="text"
                  className="dock-demo-input"
                  value={selected.operator.company ?? ''}
                  onChange={(e) => patchOperator({ company: e.target.value })}
                />
              </label>
            </div>

            <div className="dock-demo-grid-2">
              <label className="dock-demo-label">
                Email
                <input
                  type="email"
                  className="dock-demo-input"
                  placeholder="ops@example.com"
                  value={selected.operator.email ?? ''}
                  onChange={(e) => patchOperator({ email: e.target.value })}
                />
              </label>
              <label className="dock-demo-label">
                Phone
                <input
                  type="tel"
                  className="dock-demo-input"
                  value={selected.operator.phone ?? ''}
                  onChange={(e) => patchOperator({ phone: e.target.value })}
                />
              </label>
            </div>
          </Collapsible>

          {/* ── 8. Status ── */}
          <Collapsible
            title="Status"
            sub={<span className="dock-demo-tag dock-demo-tag--inline">operator-pushed</span>}
          >
            <div className="dock-demo-status-grid">
              <div className="dock-demo-status">
                <span className="dock-demo-status__label">Battery</span>
                <span className="dock-demo-status__value">
                  {selected.status?.batteryPct !== undefined
                    ? `${selected.status.batteryPct}%`
                    : '—'}
                </span>
              </div>
              <div className="dock-demo-status">
                <span className="dock-demo-status__label">Last seen</span>
                <span className="dock-demo-status__value">
                  {selected.status?.lastSeenAt ?? '—'}
                </span>
              </div>
              <div className="dock-demo-status">
                <span className="dock-demo-status__label">Last flight</span>
                <span className="dock-demo-status__value">
                  {selected.status?.lastFlightAt ?? '—'}
                </span>
              </div>
              <div className="dock-demo-status">
                <span className="dock-demo-status__label">Health</span>
                <span className="dock-demo-status__value">
                  {selected.status?.health ?? '—'}
                </span>
              </div>
            </div>
            <p className="dock-demo-hint">
              In production these fields update from a webhook the operator
              pushes — see the live-view section in the dispatch share link.
            </p>
          </Collapsible>

          {/* ── 9. Actions ── */}
          <section className="dock-demo-section dock-demo-section--footer">
            <div className="dock-demo-actions">
              <button
                type="button"
                className="dock-demo-btn dock-demo-btn--primary"
                onClick={handleSendSetupChecklist}
              >
                Send setup checklist
              </button>
              <button
                type="button"
                className="dock-demo-btn"
                onClick={handleTestKmz}
              >
                Send test KMZ
              </button>
              <button
                type="button"
                className="dock-demo-btn"
                onClick={handleCalibrate}
              >
                Mark calibrated
              </button>
            </div>
            <span className="dock-demo-footer-hint">
              Demo only — see <code>docs/DOCK_MISSION_PLAN.md</code> for the full design.
            </span>
          </section>
        </>
      )}
      </div>
    </>
  );
}
